import { useState, useEffect } from "react";
import { Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import { 
  LayoutDashboard, FileText, LogOut, Menu, TrendingUp, ChevronDown
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Progress } from "../components/ui/progress";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";

const LOGO_URL = "/logo.png";

const LEAD_STATUSES = [
  "New / Not Contacted",
  "Lead Contacted",
  "Appointment Booked",
  "Documents Submitted",
  "Approved",
  "Deal Closed / Delivered"
];

const STATUS_PROGRESS = {
  "New / Not Contacted": 0,
  "Lead Contacted": 20,
  "Appointment Booked": 40,
  "Documents Submitted": 60,
  "Approved": 80,
  "Deal Closed / Delivered": 100
};

const STATUS_COLORS = {
  "New / Not Contacted": "bg-blue-100 text-blue-800",
  "Lead Contacted": "bg-yellow-100 text-yellow-800",
  "Appointment Booked": "bg-purple-100 text-purple-800",
  "Documents Submitted": "bg-orange-100 text-orange-800",
  "Approved": "bg-cyan-100 text-cyan-800",
  "Deal Closed / Delivered": "bg-green-100 text-green-800"
};

const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { logout, user } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const navItems = [
    { path: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { path: "/dashboard/leads", icon: FileText, label: "My Leads" },
  ];

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={onClose} />
      )}
      
      <aside className={`fixed left-0 top-0 h-full w-64 bg-[#F9F7F2] border-r border-[#E5E7EB] z-50 transform transition-transform duration-300 ${isOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}>
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-[#E5E7EB]">
            <Link to="/">
              <img src={LOGO_URL} alt="Trish Drives Honda" className="h-12" />
            </Link>
          </div>
          
          <nav className="flex-1 p-4 space-y-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path || 
                (item.path !== "/dashboard" && location.pathname.startsWith(item.path));
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`sidebar-link ${isActive ? "active" : ""}`}
                  onClick={onClose}
                  data-testid={`nav-${item.label.toLowerCase().replace(" ", "-")}`}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </Link>
              );
            })}
          </nav>
          
          <div className="p-4 border-t border-[#E5E7EB]">
            <div className="flex items-center gap-3 mb-4 px-4">
              <div className="w-10 h-10 rounded-full bg-[#8D7B68] flex items-center justify-center text-white font-medium">
                {user?.name?.charAt(0) || "U"}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-[#2C2420] truncate">{user?.name}</p>
                <p className="text-xs text-[#6B5E55] truncate">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="sidebar-link w-full text-red-600 hover:bg-red-50 hover:text-red-700"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              Logout
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

const Header = ({ onMenuClick }) => {
  const { user } = useAuth();
  
  return (
    <header className="sticky top-0 z-40 bg-white border-b border-[#E5E7EB] h-16 flex items-center px-6">
      <button onClick={onMenuClick} className="lg:hidden mr-4 p-2 hover:bg-[#F2EFE9] rounded-lg">
        <Menu className="w-5 h-5" />
      </button>
      
      <div className="flex-1">
        <h1 className="font-heading text-xl font-semibold text-[#2C2420]">Sales Dashboard</h1>
      </div>
      
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-full bg-[#8D7B68] flex items-center justify-center text-white text-sm font-medium">
          {user?.name?.charAt(0) || "U"}
        </div>
        <span className="hidden md:inline text-sm font-medium text-[#2C2420]">{user?.name}</span>
      </div>
    </header>
  );
};

// User Dashboard Overview
const UserDashboardOverview = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API}/analytics`, {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        setAnalytics(response.data);
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, []);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-JM', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-12 h-12 spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="user-dashboard-overview">
      <div>
        <h2 className="font-heading text-2xl font-bold text-[#2C2420] mb-6">My Performance</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="stat-card">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-[#8D7B68]/10 flex items-center justify-center">
                <FileText className="w-6 h-6 text-[#8D7B68]" />
              </div>
            </div>
            <p className="stat-value">{analytics?.total_leads || 0}</p>
            <p className="stat-label">Assigned Leads</p>
          </div>
          
          <div className="stat-card">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <p className="stat-value">{analytics?.closed_deals || 0}</p>
            <p className="stat-label">Closed Deals</p>
          </div>
          
          <div className="stat-card">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-[#D4A373]/20 flex items-center justify-center">
                <span className="text-lg font-bold text-[#8D7B68]">$</span>
              </div>
            </div>
            <p className="stat-value">{formatCurrency(analytics?.total_sales)}</p>
            <p className="stat-label">My Sales</p>
          </div>
        </div>
      </div>
      
      <div className="dashboard-card">
        <h3 className="font-heading text-lg font-semibold text-[#2C2420] mb-4">Leads by Status</h3>
        <div className="space-y-3">
          {LEAD_STATUSES.map((status) => (
            <div key={status} className="flex items-center justify-between">
              <span className="text-sm text-[#6B5E55]">{status}</span>
              <Badge className={STATUS_COLORS[status]}>
                {analytics?.leads_by_status?.[status] || 0}
              </Badge>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// User Leads
const UserLeads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  const fetchLeads = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/leads`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setLeads(response.data);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, []);

  const handleStatusChange = async (leadId, newStatus, saleValue = null) => {
    try {
      const token = localStorage.getItem("token");
      const data = { status: newStatus };
      if (saleValue !== null) data.sale_value = saleValue;
      
      await axios.patch(`${API}/leads/${leadId}`, data, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success("Status updated");
      fetchLeads();
      setIsDetailOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update");
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-6" data-testid="user-leads">
      <h2 className="font-heading text-2xl font-bold text-[#2C2420]">My Leads</h2>
      
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-12 h-12 spinner"></div>
        </div>
      ) : leads.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border border-[#E5E7EB]">
          <FileText className="w-12 h-12 text-[#6B5E55] mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-[#2C2420] mb-2">No leads assigned</h3>
          <p className="text-[#6B5E55]">Leads assigned to you will appear here</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr className="bg-[#F9F7F2]">
                  <th>Name</th>
                  <th>Contact</th>
                  <th>Vehicle</th>
                  <th>Status</th>
                  <th>Progress</th>
                  <th>Date</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {leads.map((lead) => (
                  <tr key={lead.lead_id} className="cursor-pointer" onClick={() => { setSelectedLead(lead); setIsDetailOpen(true); }}>
                    <td className="font-medium text-[#2C2420]">{lead.full_name}</td>
                    <td>
                      <div className="text-sm">
                        <p>{lead.email}</p>
                        <p className="text-[#6B5E55]">{lead.phone}</p>
                      </div>
                    </td>
                    <td className="text-[#6B5E55]">{lead.vehicle_interested}</td>
                    <td>
                      <Badge className={STATUS_COLORS[lead.status]}>
                        {lead.status}
                      </Badge>
                    </td>
                    <td>
                      <div className="w-24">
                        <Progress value={lead.status_progress} className="h-2" />
                        <span className="text-xs text-[#6B5E55]">{lead.status_progress}%</span>
                      </div>
                    </td>
                    <td className="text-[#6B5E55]">{formatDate(lead.created_at)}</td>
                    <td><ChevronDown className="w-4 h-4" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {/* Lead Detail Modal */}
      <UserLeadDetailModal
        lead={selectedLead}
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        onStatusChange={handleStatusChange}
      />
    </div>
  );
};

const UserLeadDetailModal = ({ lead, isOpen, onClose, onStatusChange }) => {
  const [newStatus, setNewStatus] = useState("");
  const [saleValue, setSaleValue] = useState("");

  useEffect(() => {
    if (lead) {
      setNewStatus(lead.status);
      setSaleValue(lead.sale_value || "");
    }
  }, [lead]);

  const handleSave = async () => {
    if (newStatus === "Deal Closed / Delivered" && !saleValue) {
      toast.error("Sale value is required when closing a deal");
      return;
    }
    const saleValueNum = saleValue ? parseFloat(saleValue) : null;
    await onStatusChange(lead.lead_id, newStatus, saleValueNum);
  };

  if (!lead) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-white" data-testid="user-lead-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-[#2C2420]">
            Lead Details
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-[#6B5E55]">Name</Label>
              <p className="font-medium">{lead.full_name}</p>
            </div>
            <div>
              <Label className="text-sm text-[#6B5E55]">Phone</Label>
              <p className="font-medium">{lead.phone}</p>
            </div>
            <div>
              <Label className="text-sm text-[#6B5E55]">Email</Label>
              <p className="font-medium">{lead.email}</p>
            </div>
            <div>
              <Label className="text-sm text-[#6B5E55]">Vehicle</Label>
              <p className="font-medium">{lead.vehicle_interested}</p>
            </div>
            <div>
              <Label className="text-sm text-[#6B5E55]">Preferred Date</Label>
              <p className="font-medium">{lead.preferred_date} at {lead.preferred_time}</p>
            </div>
            <div>
              <Label className="text-sm text-[#6B5E55]">Source</Label>
              <p className="font-medium">{lead.lead_source}</p>
            </div>
          </div>
          
          <div className="border-t border-[#E5E7EB] pt-4">
            <Label className="form-label">Update Status</Label>
            <Select value={newStatus} onValueChange={setNewStatus}>
              <SelectTrigger className="form-input">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LEAD_STATUSES.map(status => (
                  <SelectItem key={status} value={status}>{status}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {newStatus === "Deal Closed / Delivered" && (
              <div className="mt-4">
                <Label className="form-label">Sale Value (USD) *</Label>
                <Input
                  type="number"
                  placeholder="Enter sale value"
                  value={saleValue}
                  onChange={(e) => setSaleValue(e.target.value)}
                  className="form-input"
                />
              </div>
            )}
            
            <div className="mt-4">
              <Progress value={STATUS_PROGRESS[newStatus]} className="h-3" />
              <span className="text-sm text-[#6B5E55]">{STATUS_PROGRESS[newStatus]}%</span>
            </div>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} className="btn-primary">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main User Dashboard
const UserDashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#F9F7F2]" data-testid="user-dashboard">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      <div className="lg:ml-64">
        <Header onMenuClick={() => setSidebarOpen(true)} />
        
        <main className="p-6">
          <Routes>
            <Route index element={<UserDashboardOverview />} />
            <Route path="leads" element={<UserLeads />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default UserDashboard;
